<template>
  <div :id="id + '_fs'" :class="_fs_class">
    <cat-label
      :label-id="id + '_lbl'"
      :for-id="id"
      :label="label"
      :optional="!mandatory"
      :placeholder="offscreenLabel"
      :errors="errors"
      :readonly="readonly"
    ></cat-label>
    <cat-hint :id="id + '_hint'" :text="hint" />
    <slot></slot>
    <div :class="_holder_class">
      <span class="dropdown-indicator" tabindex="-1" aria-hidden="true"></span>
      <input
        :id="id"
        ref="inputElement"
        type="text"
        :name="id"
        data-selectized="true"
        :data-allowNew="allowNew ? allowNew : undefined"
        :aria-describedby="hint ? id + '_hint' : null"
        :autocomplete="autofillToken"
      />
    </div>
    <slot name="after-input"></slot>
    <cat-errors v-if="errors && errors.length > 0" :errors="errors" />
    <div role="presentation" class="spacer"></div>
  </div>
</template>

<script lang="ts">
import jQuery from "jquery";

import "@/plugins/selectize/plugin_a11y.js";
import "@/plugins/selectize/remove_button_a11y.js";
import "@/plugins/selectize/custom_class.js";
import "@/plugins/selectize/auto_position.js";

import { defineComponent, inject } from "vue";
import { defaultResolveTxt } from "@/components/field-element";
import CatErrors from "@/components/catErrors/catErrors.vue";
import CatLabel from "@/components/utilities/cat-label.vue";
import CatHint from "@/components/utilities/hint.vue";

export default defineComponent({
  setup() {
    // Inject the global value
    const resolveTxt = inject("resolveTxt", defaultResolveTxt);
    const displayParams = inject("displayParams", false);

    return {
      resolveTxt,
      displayParams
    };
  },
  name: "CatAttributeMultiselectRemote",
  components: {
    CatErrors,
    CatLabel,
    CatHint
  },
  props: {
    id: {
      type: String,
      description: "Assigns an id to the multi select component"
    },
    errors: {
      type: Array,
      description: "Displays multi select error(s)"
    },
    label: {
      type: String,
      description: "Assigns a label to the multi select component."
    },
    items: {
      type: [Object],
      description: "Determines items that can be added to the input field."
    },
    hint: {
      type: String,
      description: "Adds hint text."
    },
    placeholder: {
      type: String,
      description: "Adds placeholder text."
    },
    offscreenLabel: {
      type: String,
      description:
        "Used by screen readers and assistive technologies to provide label and context for accessibility."
    },
    autofillToken: {
      type: String,
      default: "off",
      description:
        "The autofill token to be set on the input text element in the multi select."
    },
    value: {
      type: String,
      description: "Determines value of text."
    },
    mandatory: {
      type: Boolean,
      default: false,
      description: "If true field can't be empty"
    },
    size: {
      type: String,
      description:
        "Determines size of the input wrapper (small, medium, large)."
    },
    readonly: {
      type: Boolean,
      description:
        "Renders entries as list items and prevents editing (for configuration)."
    },
    autocompleteFunction: {
      type: Function,
      description: "Suggests remote entries when start typing."
    },
    disableLookup: {
      type: Boolean,
      default: false,
      description: "Disables lookup and all new values will be created."
    },
    allowNew: {
      type: Boolean,
      description: "Deprecated."
    },
    minInputLength: {
      type: Number,
      default: 2,
      description: "Determines minimum input."
    },
    maxItems: {
      type: Number,
      default: null,
      description: "Determines maximum input."
    }
  },
  data: function () {
    return {
      model: this.value ? this.value : "",
      selectize: null,
      textboxId: this.id + "-textbox"
    };
  },
  computed: {
    _fs_class: function () {
      return {
        fieldset: true,
        "cat-select-multi-remote": true,
        "cat-select-multi": true,
        "autosuggest-remote": true,
        "type-to-open-options": this.minInputLength > 0,
        required: this.mandatory,
        optional: !this.mandatory,
        "readonly-list": this.readonly,
        "selectize-disabled-lookup": this.disableLookup
      };
    },
    _holder_class: function () {
      return {
        // 'select-holder': !this.autocomplete,
        "ac-holder": true,
        small: this.size == "small",
        medium: !this.size || this.size == "medium",
        large: this.size == "large"
      };
    }
  },
  watch: {
    value: function (newval, oldvalue) {
      if (this.model != newval) {
        this.model = newval;
        this.selectize.setValue(newval, true);
      }
    },
    items: function (newValues) {
      this.refreshOptions();
    }
  },

  mounted: function () {
    const _this = this;
    const options = {
      load: (query, callback) => {
        if (query.length < this.minInputLength) return callback();
        if (this.disableLookup) {
          return {};
        } else {
          const res = this.autocompleteFunction(query);
          res.then((data) => {
            callback(
              data.results.map((item) => {
                return {
                  value: item.id,
                  text: item.text
                };
              })
            );
          });
        }
      },
      onChange: (val) => {
        const options = _this.selectize.options;
        const text = options[val] ? options[val].text : null;
        const newItems = val.split(",");
        const existingItems = _this.model.split(",");
        var newValues = [];
        var delItems = [];

        newItems.forEach((v) => {
          if (v !== "" && existingItems.indexOf(v) === -1) {
            newValues.push(v);
          }
        });
        existingItems.forEach((v) => {
          if (v !== "" && newItems.indexOf(v) === -1) {
            delItems.push(v);
          }
        });
        newValues.forEach((v) => {
          const text = options[val] ? options[val].text : null;
          if (text) {
            this.autocompleteChange(val, v, text);
          } else {
            this.autocompleteCreate(val, v);
          }
        });
        delItems.forEach((v) => {
          _this.autocompleteRemove(val, v);
        });
        this.model = val;
      },
      delimiter: ",",
      valueField: "value",
      labelField: "text",
      sortField: "text",
      persist: false,
      items: this.model.split(","),
      options: this.items,
      hideSelected: true,
      placeholder: this.placeholder,
      plugins: {
        plugin_custom_class: {
          errorClass: "error"
        },
        remove_button_a11y: {
          title: "Remove",
          label: ""
        },
        selectize_a11y: {
          openOnClick: true,
          textboxId: this.textboxId,
          autofillToken: this.autofillToken,
          label: this.label || this.offscreenLabel || this.placeholder,
          labelId: this.label ? this.id + "_lbl" : null,
          id: this.id,
          allowNew: this.allowNew
        },
        auto_position: true
      },
      preload: false,
      maxItems: this.maxItems,
      closeAfterSelect: true
    };
    // if create function is present then selectize will allow creation of entries
    if (this.allowNew) {
      options.create = function (input) {
        return {
          value: input,
          text: input,
          error: false
        };
      };
    }
    const s = jQuery(this.$refs.inputElement).selectize(options);
    if (s && s.length > 0) {
      this.selectize = s[0].selectize;
      this.selectize.on("focus", this._onFocus);
    }
  },
  methods: {
    _onChange: function (values, value, text) {
      // console.log("changed", value, text);
      this.$emit("ui-change", {
        value: value,
        values: values,
        id: this.id ? this.id : "",
        el: this.$el,
        text: text
      });
    },

    _onBlur: function (event) {
      this.$emit("ui-blur", {
        event: event,
        id: this.id
      });
    },
    _onFocus: function (event) {
      if (
        !this.selectize.isFull() &&
        this.minInputLength === 0 &&
        !this.disableLookup
      ) {
        this.selectize.load((callback) => {
          const res = this.autocompleteFunction(this.model);
          res.then((data) => {
            callback(
              data.results.map((item) => {
                return {
                  value: item.id,
                  text: item.text
                };
              })
            );
          });
        });
      }
    },
    autocompleteChange: function (values, value, text) {
      if (value == this.model) {
        return;
      }
      this._onChange(values, value, text);
    },
    autocompleteRemove: function (values, value) {
      this.$emit("ui-remove", {
        value: value,
        values: values,
        id: this.id ? this.id : "",
        el: this.$el
      });
    },
    autocompleteCreate: function (text) {
      if (!this.allowNew || text == this.model) {
        return;
      }

      this.$emit("ui-create", {
        id: this.id ? this.id : "",
        text: text,
        el: this.$el,
        value: text
      });
    },
    /**
     * This method will refresh the dropdown options and selected items from the content of the props 'items'
     */
    refreshOptions: function () {
      this.selectize.clearCache("option");
      this.selectize.clearOptions();
      // Add all the options back in the drop-down
      const t = this;
      this.items.forEach(function (item) {
        t.selectize.addOption(t.getSelectizeListOption(item));
        t.selectize.addItem(item.value, true);
        // Update selected options to assign new value if there are changes to custom value (error)
        t.selectize.updateOption(item.value, t.getSelectizeListOption(item));
      });
      this.selectize.refreshOptions(true);
    },
    /**
     * Return the option object for the selectize
     * @param item Item object from the props 'items' list
     * @returns {{text: *, value: *, error: *}}
     */
    getSelectizeListOption: function (item) {
      return {
        text: item.text,
        value: item.value,
        error: item.error ? item.error : false
      };
    }
  }
});
</script>
